import React from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import DeepResearchIconDark from '../../../assets/deep-research-icon-dark-mode.svg';
import DeepResearchIconLight from '../../../assets/deep-research-icon-light-mode.svg';
import MaxModeAnimationLight from '../../../assets/max-atom-optimized.gif';
import MaxModeAnimationDark from '../../../assets/max-atom-white-optimized.gif';
import RovoChatBubblesIcon from '../../../assets/rovo-chat-bubbles.svg';
import TaskDoneIcon from '../../../assets/task-done.svg';
import { CustomGreetingCard } from '../greeting-card';

import { getGreetingModes, type GreetingPresentationMode } from './getGreetingModes';
import messages from './messages';

type RovoGreetingProps = {
	firstName: string;
	isDeepResearchEnabled: boolean;
	/** Whether task mode is enabled */
	isTaskModeEnabled?: boolean;
	/** Resolved presentation branch when the caller also owns chat-mode selection. */
	greetingMode?: GreetingPresentationMode;
	/**
	 * Optional custom heading. Overrides the active mode's default heading
	 * (normal / deep-research / task); falls back to the mode default when unset.
	 */
	greetingText?: string;
	/**
	 * Optional illustration overriding the active mode's default (e.g. a
	 * personality-flavored greeting). The mode's default illustration is kept
	 * when unset; mode-specific animation and entrance direction are preserved.
	 */
	illustrationSrc?: string;
	/** Dark-theme illustration. Falls back to `illustrationSrc` when unset. */
	illustrationSrcDark?: string;
	/** Size of the heading text. @default 'xlarge' */
	headingSize?: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
	/**
	 * Controls greeting visibility during mini modal resize.
	 * - `undefined` (default): normal mode — uses entrance animation, always visible when rendered.
	 * - `true` / `false`: resize mode — uses CSS transitions for smooth fade + height collapse.
	 */
	isRevealedByResize?: boolean | null;
	/** Controls greeting visibility by default when in resize mode */
	isRevealedByDefault?: boolean;
};

export const RovoGreeting = ({
	firstName,
	isDeepResearchEnabled,
	isTaskModeEnabled,
	greetingMode,
	greetingText,
	illustrationSrc,
	illustrationSrcDark,
	headingSize,
	isRevealedByResize,
	isRevealedByDefault,
}: RovoGreetingProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();

	const presentationMode =
		greetingMode ??
		getGreetingModes({
			deepResearchEnabled: isDeepResearchEnabled,
			taskModeEnabled: Boolean(isTaskModeEnabled),
		}).presentationMode;
	const isDesignRefresh =
		presentationMode === 'taskMode' && fg('rovo_chat_task_mode_design_refresh');
	const maxModeAnimationSrc = colorMode === 'dark' ? MaxModeAnimationDark : MaxModeAnimationLight;

	const defaultGreeting = formatMessage(messages.defaultGreetingText, { name: firstName });
	const displayGreeting = greetingText || defaultGreeting;
	// Changing the key lets the default greeting exit and the custom greeting enter
	const customGreetingAnimationSuffix = greetingText === undefined ? '' : '-custom';

	if (presentationMode === 'deepResearch') {
		return (
			<CustomGreetingCard
				src={illustrationSrc ?? DeepResearchIconLight}
				srcDark={illustrationSrcDark ?? DeepResearchIconDark}
				title={greetingText ?? formatMessage(messages.deepResearchGreetingText)}
				animationKey={`deep-research${customGreetingAnimationSuffix}`}
				entranceDirection="top"
				exitDirection="top"
				headingSize={headingSize}
				isRevealedByResize={isRevealedByResize}
				isRevealedByDefault={isRevealedByDefault}
				firstName={firstName}
			/>
		);
	}

	if (presentationMode === 'taskMode') {
		return (
			<CustomGreetingCard
				src={illustrationSrc ?? (isDesignRefresh ? maxModeAnimationSrc : TaskDoneIcon)}
				srcDark={illustrationSrcDark}
				title={
					greetingText ??
					formatMessage(
						isDesignRefresh
							? messages.taskModeDesignRefreshGreetingText
							: messages.taskModeGreetingText,
					)
				}
				animationKey={`task-mode${customGreetingAnimationSuffix}`}
				entranceDirection="top"
				exitDirection="top"
				headingSize={headingSize}
				headingPaddingInline="space.400"
				isRevealedByResize={isRevealedByResize}
				isRevealedByDefault={isRevealedByDefault}
				firstName={firstName}
				{...(isDesignRefresh ? { imageHeight: 67, imageWidth: 67 } : {})}
			/>
		);
	}

	return (
		<CustomGreetingCard
			src={illustrationSrc ?? RovoChatBubblesIcon}
			srcDark={illustrationSrcDark}
			title={displayGreeting}
			firstName={firstName}
			animationKey={`normal-chat${customGreetingAnimationSuffix}`}
			entranceDirection="bottom"
			exitDirection="top"
			headingSize={headingSize}
			isRevealedByResize={isRevealedByResize}
			isRevealedByDefault={isRevealedByDefault}
		/>
	);
};
